import {
  ErrorOutline as ErrorIcon,
  InfoOutlined as InfoIcon,
} from '@mui/icons-material';
import { Box, IconButton, Table, TableBody, TableRow } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { NoResultAlert } from 'src/components/noResultAlert';
import { Progress } from 'src/components/progress/Progress';
import { CustomTableHead } from 'src/components/table/LegacyTable/LegacyCustomTable.styled';
import { CustomTooltip } from 'src/components/tooltip/CustomTooltip';
import { lang } from 'src/utils';

import { useListPublicationListPage } from '../../hooks/ListPublicationPage.hook';
import {
  IPublicationList,
  SORT_DIRECTION,
  STATUS_LIST,
} from '../../PublicationPage.interface';
import {
  ArrowIcon,
  CustomTableCell,
  CustomTableRow,
  InViewBoxContainer,
  KeyboardArrowDownIconStyled,
  KeyboardArrowUpIconStyled,
  OfflineStatusContainer,
  PublicationStatusChip,
  SortIcons,
  StatusContainer,
  StatusDot,
  StyledTooltipContainer,
  TableBodyContainer,
  TableHeadCell,
} from '../../PublicationPage.style';

export const PublicationListGridTable = () => {
  const fallbackHeaderHeight = 81;
  const fallbackTableContainerHeight = '92vh';
  const [tableContainerHeight, setTableContainerHeight] = useState('auto');
  const { ref: inViewRef, inView } = useInView({ threshold: 0.1 });
  const tableRef = useRef<HTMLDivElement>(null);
  const {
    isLoading,
    getNextListPublicationPage,
    columns,
    listPublication,
    hasNextPage,
    handleSort,
    activeColumn,
    sorting,
  } = useListPublicationListPage();

  useEffect(() => {
    let timerId: string | number | NodeJS.Timeout | undefined;

    const calculateDynamicHeight = () => {
      let headerHeight;

      if (tableRef.current) {
        const rect = tableRef.current.getBoundingClientRect();
        headerHeight = rect.top + window.scrollY;
      } else {
        timerId = setTimeout(calculateDynamicHeight, 500);
        headerHeight = fallbackHeaderHeight;
      }

      const viewportHeight = window.innerHeight;
      const dynamicHeight = Math.round(viewportHeight - headerHeight);

      setTableContainerHeight(
        dynamicHeight > 0 ? `${dynamicHeight}px` : fallbackTableContainerHeight,
      );
    };

    calculateDynamicHeight();

    window.addEventListener('resize', calculateDynamicHeight);

    return () => {
      window.removeEventListener('resize', calculateDynamicHeight);
      clearTimeout(timerId);
    };
  }, []);

  useEffect(() => {
    if (inView) {
      getNextListPublicationPage();
    }
  }, [getNextListPublicationPage, inView]);

  if (isLoading) {
    return <Progress />;
  }
  const hasData =
    listPublication &&
    listPublication?.pages.flatMap((page) => page.data).length > 0;
  const showChip = (str: string) => {
    if (!str) return null;

    let status = '';
    let statusString = '';
    let showErrorIcon = false;
    let tooltipMessage = '';

    if (str === STATUS_LIST.UP_TO_DATE) {
      statusString = lang.uptoDate;
      status = 'success';
    } else if (str === STATUS_LIST.UPDATE_IN_PROGRESS) {
      statusString = lang.updateInProgress;
      status = 'info';
    } else if (str === STATUS_LIST.OUT_OF_DATE) {
      statusString = lang.outOfDate;
      status = 'warning';
    } else if (str === STATUS_LIST.FAILED) {
      statusString = lang.failed;
      status = 'error';
      showErrorIcon = true;
      tooltipMessage = lang.locationErrorMessage;
    }

    return (
      <Box display="flex" alignItems="center" gap={1}>
        <PublicationStatusChip
          status={status}
          chipName={statusString}
          size="small"
        />
        {showErrorIcon && (
          <CustomTooltip
            title={tooltipMessage || 'Error'}
            placement="right"
            width={300}
            skipValidateOverflow={true}>
            <ErrorIcon color="error" fontSize="medium" />
          </CustomTooltip>
        )}
      </Box>
    );
  };

  const addLocationSataus = (data: IPublicationList) => {
    const isOnline = data.systemStatus === STATUS_LIST.ONLINE;

    return (
      <>
        <CustomTooltip
          title={data.locationName}
          placement="bottom"
          width={500}
          overflowLineNumber={1}>
          <StyledTooltipContainer>{data.locationName}</StyledTooltipContainer>
        </CustomTooltip>

        {data.systemStatus && (
          <StatusContainer isOnline={isOnline}>
            <StatusDot isOnline={isOnline} />
            <StatusContainer isOnline={isOnline}>
              {data.systemStatus}
            </StatusContainer>
            {!isOnline && (
              <OfflineStatusContainer component="span">
                {data.locationReported && ` since ${data.locationReported}`}
              </OfflineStatusContainer>
            )}
          </StatusContainer>
        )}
      </>
    );
  };

  return (
    <>
      {hasData ? (
        <div ref={tableRef}>
          <TableBodyContainer height={tableContainerHeight}>
            <Table stickyHeader>
              <CustomTableHead>
                <TableRow>
                  {columns.map((column) => (
                    <CustomTableCell
                      key={column.key}
                      isdoublewidth={column.key}>
                      <TableHeadCell>
                        {column.label}
                        {column.sortIcon && (
                          <SortIcons>
                            <ArrowIcon
                              onClick={() =>
                                handleSort(column.key, SORT_DIRECTION.ASC)
                              }>
                              <KeyboardArrowUpIconStyled
                                active={
                                  activeColumn === column.key &&
                                  sorting.order === SORT_DIRECTION.ASC
                                }
                              />
                            </ArrowIcon>
                            <ArrowIcon
                              onClick={() =>
                                handleSort(column.key, SORT_DIRECTION.DESC)
                              }>
                              <KeyboardArrowDownIconStyled
                                active={
                                  activeColumn === column.key &&
                                  sorting.order === SORT_DIRECTION.DESC
                                }
                              />
                            </ArrowIcon>
                          </SortIcons>
                        )}
                        {column.label === lang.lastModified && (
                          <CustomTooltip
                            placement="left"
                            width={500}
                            title={lang.lastModifiedInfo}
                            skipValidateOverflow={true}>
                            <IconButton disableRipple>
                              <InfoIcon />
                            </IconButton>
                          </CustomTooltip>
                        )}
                      </TableHeadCell>
                    </CustomTableCell>
                  ))}
                </TableRow>
              </CustomTableHead>
              <TableBody>
                {!isLoading &&
                  listPublication?.pages.map((page) =>
                    page.data.map((row, id) => (
                      <CustomTableRow key={id}>
                        {columns.map((column) => (
                          <CustomTableCell
                            key={column.key}
                            isdoublewidth={column.key}>
                            {column.label === lang.status
                              ? showChip(
                                  row[column.key as keyof IPublicationList],
                                )
                              : column.label === lang.locationName
                              ? addLocationSataus(row)
                              : row[column.key as keyof IPublicationList]}
                          </CustomTableCell>
                        ))}
                      </CustomTableRow>
                    )),
                  )}
              </TableBody>
            </Table>
            {hasNextPage && (
              <InViewBoxContainer>
                <Box ref={inViewRef} />
              </InViewBoxContainer>
            )}
          </TableBodyContainer>
        </div>
      ) : (
        <NoResultAlert />
      )}
    </>
  );
};
